import { PaywallModalCentered } from './components.tsx/paywall-modal-centered.tsx';

(() => {
    // Version tracking for deployment verification
    const PAYWALL_VERSION = '1.3.0';

    // Create AbortController for event cleanup
    const controller = new AbortController();
    const { signal } = controller;

    // Function to detect and extract the entire Zephr element from article body
    function detectAndExtractZephrElement() {
        // Look for the main Zephr form container with multiple selectors
        const selectors = [
            '.zephr-form-container',
            '#zephr-form-RtjYpv', 
            '#zephr-form-siiIwz',
            '[id^="zephr-form-"]',
            '[id*="zephr-form"]'
        ];
        
        let zephrElement = null;
        for (const selector of selectors) {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                zephrElement = elements[0];
                break;
            }
        }
        
        // Check if this is a complete profile form and exclude it
        if (zephrElement && isCompleteProfileForm(zephrElement)) {
            zephrElement = null;
        }
        
        if (zephrElement) {
            return zephrElement;
        }
        
        // Fallback: look for payment options container and find parent
        const paymentContainer = document.querySelector('.payment-options-container');
        if (paymentContainer) {
            // Find the parent Zephr container
            let parent = paymentContainer.parentElement;
            let depth = 0;
            while (parent && depth < 10) { // Prevent infinite loops
                if (parent.classList.contains('zephr-form-container') || 
                    parent.id.startsWith('zephr-form-') ||
                    parent.id.includes('zephr-form')) {
                    // Check if this is a complete profile form and exclude it
                    if (!isCompleteProfileForm(parent)) {
                        return parent;
                    }
                }
                parent = parent.parentElement;
                depth++;
            }
        }
        
        // Additional fallback: look for any element with zephr classes
        const zephrElements = document.querySelectorAll('[class*="zephr"], [id*="zephr"]');
        if (zephrElements.length > 0) {
            // Return the first one that looks like a main container and is not a complete profile form
            for (const element of zephrElements) {
                if ((element.classList.contains('zephr-form-container') || 
                    element.id.includes('zephr-form')) && !isCompleteProfileForm(element)) {
                    return element;
                }
            }
        }
        
        return null;
    }

    // Function to check if a Zephr element is a complete profile form
    function isCompleteProfileForm(element) {
        // Check if it's inside a zephr-zone-popup dialog (complete profile modal)
        if (element.closest('.zephr-zone-popup')) {
            return true;
        }
        
        // Check for complete profile specific content
        const formContent = element.querySelector('.zephr-form-content');
        if (formContent) {
            // Look for "Complete Your Profile" text
            const profileText = formContent.textContent || '';
            if (profileText.includes('Complete Your Profile') || 
                profileText.includes('Complete Your') && profileText.includes('Profile')) {
                return true;
            }
            
            // Look for profile-specific form fields
            const hasCompanyField = formContent.querySelector('input[name="company"], select[name="company"]');
            const hasTitleField = formContent.querySelector('input[name="title"], select[name="title"]');
            const hasBusinessTypeField = formContent.querySelector('select[name="business-type"]');
            const hasJobFunctionField = formContent.querySelector('select[name="job-function"]');
            
            if (hasCompanyField && hasTitleField && hasBusinessTypeField && hasJobFunctionField) {
                return true;
            }
        }
        
        return false;
    }

    // Function to create a test Zephr element for testing
    function createTestZephrElement() {
        const testElement = document.createElement('div');
        testElement.id = 'zephr-form-test';
        testElement.className = 'zephr-form-container';
        testElement.innerHTML = `
            <div class="zephr-form zephr-payment-form">
                <div class="zephr-form-content">
                    <div class="zephr-form-custom-text">
                        <h2 style="color: #f53c6d;">Subscribe today!</h2>
                        <p>THE INSIGHTS AND INTELLIGENCE YOU NEED TO SUCCEED</p>
                    </div>
                    <div class="payment-options-container">
                        <div class="payment-option-wrapper">
                            <div class="box-container">
                                <div class="box-content">
                                    <h1>$20</h1>
                                    <h3>DIGITAL MONTHLY</h3>
                                    <p>Billed every four weeks, cancel any time.</p>
                                </div>
                                <div class="box-bottom">
                                    <button class="zephr-form-button">SUBSCRIBE</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
        return testElement;
    }

    // Function to move entire Zephr element to modal
    function moveZephrElementToModal(zephrElement) {
        if (!zephrElement) return;
        
        // Find the paywall modal content container (try new ID first, then fallback to old ID)
        let paywallModalContent = document.getElementById('adw-paywall-modal-content');
        if (!paywallModalContent) {
            paywallModalContent = document.getElementById('adw-paywall-widgets');
        }
        if (!paywallModalContent) {
            return;
        }

        // Remove the entire Zephr element from the article body (only if it's in the DOM)
        if (zephrElement.parentNode) {
            zephrElement.remove();
        }

        // Clear the modal content and add the Zephr element
        paywallModalContent.innerHTML = '';
        paywallModalContent.appendChild(zephrElement);
    }

    // Wait for Zephr to be fully loaded
    function waitForZephr(callback, maxAttempts = 10) {
        let attempts = 0;
        const checkZephr = () => {
            if (window.Zephr && window.Zephr.accessDetails) {
                callback();
            } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkZephr, 100);
            }
        };
        checkZephr();
    }

    // Get scrollbar width
    function getScrollbarWidth() {
        const outer = document.createElement('div');
        outer.style.visibility = 'hidden';
        outer.style.overflow = 'scroll';
        document.body.appendChild(outer);
        
        const inner = document.createElement('div');
        outer.appendChild(inner);
        
        const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
        outer.parentNode.removeChild(outer);
        
        return scrollbarWidth;
    }

    function getScrollContainer(el) {
        let node = el && el.parentElement;
        while (node && node !== document.body && node !== document.documentElement) {
            const oy = getComputedStyle(node).overflowY;
            if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
                node.scrollHeight > node.clientHeight) {
                return node;
            }
            node = node.parentElement;
        }
        const docEl = document.documentElement;
        if (docEl.scrollHeight > docEl.clientHeight) {
            return window;
        }
        return document.body.scrollHeight > document.body.clientHeight ? document.body : window;
    }

    function getScrollTop(container) {
        if (!container || container === window) {
            return window.pageYOffset || document.documentElement.scrollTop || 0;
        }
        return container.scrollTop;
    }

    function getContainerViewportTop(container) {
        if (!container || container === window) return 0;
        return container.getBoundingClientRect().top;
    }

    // Check for test query parameter
    function shouldShowPaywallForTesting() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('show_paywall') === 'true';
    }

    // Determines whether the paywall modal should be rendered for the current user and content
    function shouldRenderPaywall() {
        const premiumLockElement = document.querySelector('.zephr_premium_article_indicator');

        const isRealZephrPremium = premiumLockElement !== null && (
            premiumLockElement.hasAttribute('data-zephr-component') ||
            premiumLockElement.hasAttribute('data-zephr-id') ||
            premiumLockElement.closest('[data-zephr-container]') !== null ||
            premiumLockElement.closest('[data-zephr-feature]') !== null ||
            premiumLockElement.closest('[data-zephr-article]') !== null
        );

        const hasZephrGradient = !!document.querySelector('.aw-article-content + div[style*="linear-gradient"]');

        const contentAccessOutcome = window.Zephr?.outcomes?.['content-access-management']?.outcomeLabel;
        const hasPremiumOutcome =
            contentAccessOutcome === 'Magazine Premium Anonymous Paywall- Payment Form' ||
            contentAccessOutcome === 'Magazine Premium Registered Paywall - Payment Form' ||
            contentAccessOutcome === 'Archive Library Anonymous Paywall- Payment Form';

        const hasPremiumArticleIndicator = !!document.querySelector('.zephr_premium_article_indicator');
        const isPremiumLocked = isRealZephrPremium || hasPremiumOutcome || hasPremiumArticleIndicator || hasZephrGradient;

        const authOverride = document.querySelector('meta[name="auth_override"]')?.getAttribute('content');
        const postType = document.querySelector('meta[name="post_type"]')?.getAttribute('content');

        const FREE_POST_TYPES = new Set([
            'adweek_wire', 'agency-culture', 'expert_info', 'partner_articles',
            'sponsored_post', 'partner_info', 'press_release', 'adw_resource',
        ]);

        if (!shouldShowPaywallForTesting() && (
            authOverride === 'free' ||
            authOverride === 'meter override' ||
            FREE_POST_TYPES.has(postType)
        )) {
            return { shouldShow: false, isUserSubscriber: false };
        }

        const isAuthenticated = window.Zephr?.accessDetails?.isAuthenticated;
        const isSubscriber = window.Zephr?.accessDetails?.isSubscriber === true;
        const userType = window.awGetUserType?.();
        const isUserSubscriber = userType === 'S' || isSubscriber;

        const hasZephrElement = !!document.querySelector('.zephr-form-container');
        const shouldShow = (isPremiumLocked && (
            !isAuthenticated ||
            (isAuthenticated && !isUserSubscriber) ||
            (userType === 'R' && !isUserSubscriber)
        )) || shouldShowPaywallForTesting() || hasZephrElement;

        return { shouldShow, isUserSubscriber };
    }

    // Generic centered modal driven by IntersectionObserver.
    // config: { triggerId, mountId, btnClickEvent }
    function initTreatmentPaywall(element, config) {
        const React = window.React;
        const ReactDOM = window.ReactDOM;
        if (!React || !ReactDOM?.client?.createRoot) return;

        // Swap the paywall element for a lightweight trigger placeholder
        const trigger = document.createElement('div');
        trigger.id = config.triggerId;
        element.replaceWith(trigger);

        // Capture the inner HTML before removing the element from the DOM
        const paywallHTML = element.innerHTML;

        // Mount the React modal into a container appended to body
        const mountContainer = document.createElement('div');
        mountContainer.id = config.mountId;
        document.body.appendChild(mountContainer);

        const root = ReactDOM.client.createRoot(mountContainer);
        let isModalOpen = false;

        function renderModal(open) {
            isModalOpen = open;
            root.render(
                React.createElement(
                    PaywallModalCentered,
                    {
                        isOpen: open,
                        onClose: () => renderModal(false),
                    },
                    React.createElement('div', {
                        dangerouslySetInnerHTML: { __html: paywallHTML },
                        className: config.contentClassName,
                    })
                )
            );
        }

        // Render closed initially so the component is mounted and ready
        renderModal(false);

        // Track .paywall-modal-btn clicks via delegation on the mount container
        mountContainer.addEventListener('click', (e) => {
            if (e.target.closest('.paywall-modal-btn')) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ event: config.btnClickEvent });
            }
        });

        // Retrigger the modal every time the trigger scrolls into view
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isModalOpen) {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'paywall-modal-view-treatment' });
                    renderModal(true);
                }
            },
            { threshold: 0 }
        );
        observer.observe(trigger);
    }

    // Initialize functionality
    function init() {
        // Prevent multiple initializations
        if (window.paywallInitialized) {
            return;
        }
        window.paywallInitialized = true;

        // Check if this is a single post/article page once at initialization
        const isSinglePost = document.body.classList.contains('single-post') ||
                           document.body.classList.contains('single-article');

        if (!isSinglePost && !shouldShowPaywallForTesting()) {
            return;
        }

        // Treatment A/B: if either is present, use the centered modal and skip the Zephr overlay flow
        const treatmentAElement = document.getElementById('treatment-a-paywall');
        const treatmentBElement = document.getElementById('treatment-b-paywall');

        if (treatmentAElement || treatmentBElement) {
            if (treatmentAElement) {
                initTreatmentPaywall(treatmentAElement, {
                    triggerId: 'treatment-a-paywall-trigger',
                    mountId: 'treatment-a-paywall-mount',
                    btnClickEvent: 'paywall-modal-existing-subp-click',
                    contentClassName: 'treatment-a-paywall-content d-flex flex-column align-items-center justify-content-center gap-4',
                });
            }
            if (treatmentBElement) {
                initTreatmentPaywall(treatmentBElement, {
                    triggerId: 'treatment-b-paywall-trigger',
                    mountId: 'treatment-b-paywall-mount',
                    btnClickEvent: 'paywall-new-subp-click',
                    contentClassName: 'treatment-b-paywall-content d-flex flex-column align-items-center justify-content-center gap-4',
                });
            }
            return;
        }

        const overlay = document.getElementById('enhanced-paywall-overlay');
        if (!overlay) {
            return;
        }

        // Wait for Zephr to be loaded before proceeding with initialization
        waitForZephr(() => {
            const scrollbarWidth = getScrollbarWidth();

            // Detect and move entire Zephr element to modal
            const zephrElement = detectAndExtractZephrElement();
            if (zephrElement) {
                moveZephrElementToModal(zephrElement);
            } else {
                // For testing: create a test Zephr element if none found
                if (shouldShowPaywallForTesting()) {
                    const testZephrElement = createTestZephrElement();
                    moveZephrElementToModal(testZephrElement);
                }
            }

            let isPaywallVisible = false;
            let isLocking = false;
            let lockPosition = 0;
            let preventScroll = false;
            let wheelEvents = [];

            let scrollContainer = window;

            function pinScroll() {
                if (scrollContainer === window) {
                    window.scrollTo(0, lockPosition);
                } else {
                    scrollContainer.scrollTop = lockPosition;
                }
            }

            // Apply gradient immediately based on user status
            (function applyInitialGradient() {
                const articleContent = document.querySelector('.aw-article-content');
                if (!articleContent) return;
                
                // Check user subscription status
                const isAuthenticated = window.Zephr?.accessDetails?.isAuthenticated;
                const isSubscriber = window.Zephr?.accessDetails?.isSubscriber === true;
                const userType = window.awGetUserType?.();
                const isUserSubscriber = userType === 'S' || isSubscriber;
                
                if (!isUserSubscriber) {
                    articleContent.classList.add('paywall-fade-gradient');
                }
            })();

            // Check for test parameter immediately after initialization
            if (shouldShowPaywallForTesting()) {
                setTimeout(() => {
                    lockScroll(getScrollTop(scrollContainer));
                }, 1000); // Small delay to ensure everything is loaded
            }

            function showEnhancedPaywall() {
                if (!isPaywallVisible) {
                    const overlay = document.getElementById('enhanced-paywall-overlay');
                    if (!overlay) {
                        return;
                    }
                    
                    // Ensure close button is properly positioned (CSS handles most styling)
                    const closeBtn = document.getElementById('paywall-close-btn');
                    if (closeBtn) {
                        // Remove any conflicting Bootstrap classes
                        closeBtn.className = '';
                    }
                    
                    // Article fade is controlled by scroll position and user status in checkScroll()
                    const content = overlay.querySelector('.enhanced-paywall-content');
                    if (!content) {
                        return;
                    }
                    let modalContent = document.getElementById('adw-paywall-modal-content');
                    if (!modalContent) {
                        modalContent = document.getElementById('adw-paywall-widgets');
                    }
                    if (!modalContent) {
                        return;
                    }

                    // Check if there's a Zephr element in the modal
                    const zephrElement = modalContent.querySelector('.zephr-form-container, [id^="zephr-form-"]');
                    if (!zephrElement) {
                        return;
                    }
                    
                    // Show overlay first
                    overlay.style.display = 'block';
                    content.style.bottom = '-100%';
                    
                    // Force reflow and add padding
                    overlay.offsetHeight;
                    const currentPadding = parseInt(getComputedStyle(document.body).paddingRight, 10) || 0;
                    document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
                    document.body.style.overflow = 'hidden';
                    
                    // Trigger animations
                    requestAnimationFrame(() => {
                        overlay.style.opacity = '1';
                        content.style.bottom = '0';
                    });

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'paywall-modal-view-control' });

                    isPaywallVisible = true;
                }
            }

            function hideEnhancedPaywall() {
                if (!isPaywallVisible) return;
                
                // Article fade is managed independently of modal visibility
                
                const overlay = document.getElementById('enhanced-paywall-overlay');
                const content = overlay.querySelector('.enhanced-paywall-content');
                
                // Start overlay animations
                content.style.bottom = '-100%';
                overlay.style.opacity = '0';
                
                // Wait for animations to complete
                const onTransitionEnd = (e) => {
                    // Only proceed if it's the overlay's opacity transition that ended
                    if (e.target !== overlay || e.propertyName !== 'opacity') return;
                    
                    overlay.style.display = 'none';
                    
                    // Reset body styles
                    document.body.style.position = '';
                    document.body.style.width = '';
                    document.body.style.top = '';
                    document.body.style.overflow = '';
                    document.body.style.paddingRight = '';
                    if (scrollContainer !== window) {
                        scrollContainer.style.overflow = '';
                    }
                    
                    // Reset scroll prevention
                    preventScroll = false;
                    isLocking = false;
                    wheelEvents = [];
                    
                    // Simple smooth scroll to top
                    if (scrollContainer === window) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    
                    // Reset state after a delay to ensure scroll completes
                    setTimeout(() => {
                        isPaywallVisible = false;
                        hasShownPaywall = false;
                        
                        // Ensure scroll is enabled
                        document.body.style.overflow = '';
                        document.documentElement.style.overflow = '';
                        document.body.style.position = '';
                        document.body.style.width = '';
                        document.body.style.top = '';
                        if (scrollContainer !== window) {
                            scrollContainer.style.overflow = '';
                        }
                        
                        // Force a reflow
                        document.body.offsetHeight;
                    }, 1000);
                    
                    overlay.removeEventListener('transitionend', onTransitionEnd);
                };
                
                overlay.addEventListener('transitionend', onTransitionEnd);
            }

            // Paywall close events with signal
            document.getElementById('paywall-close-btn').addEventListener('click', hideEnhancedPaywall, { signal });
            document.getElementById('enhanced-paywall-overlay').addEventListener('click', (e) => {
                if (e.target.id === 'enhanced-paywall-overlay') {
                    hideEnhancedPaywall();
                }
            }, { signal });

            document.querySelector('.enhanced-paywall-content').addEventListener('click', (e) => {
                e.stopPropagation();
            }, { signal });

            // Scroll handler with signal
            let lastScrollCheck = 0;
            let lastScrollTop = 0;
            let scrollSpeed = 0;
            
            function lockScroll(position) {
                if (!isLocking) {
                    isLocking = true;
                    lockPosition = position;
                    preventScroll = true;

                    pinScroll();
                    if (scrollContainer === window) {
                        document.body.style.overflow = 'hidden';
                        document.body.style.position = 'fixed';
                        document.body.style.width = '100%';
                        document.body.style.top = `-${lockPosition}px`;
                    } else {
                        scrollContainer.style.overflow = 'hidden';
                    }

                    // Show paywall immediately
                    showEnhancedPaywall();

                    const modalScrollRegion = document.querySelector('.paywall-scroll-container');
                    if (modalScrollRegion) {
                        // Remove any existing event listeners
                        modalScrollRegion.removeEventListener('wheel', handleWheel);
                        modalScrollRegion.removeEventListener('touchmove', handleTouchMove);
                        
                        // Add new event listeners
                        modalScrollRegion.addEventListener('wheel', handleWheel, { passive: true });
                        modalScrollRegion.addEventListener('touchmove', handleTouchMove, { passive: true });
                    }
                }
            }

            // Separate handler functions for better event management
            function handleWheel(e) {
                e.stopPropagation();
            }

            function handleTouchMove(e) {
                const modalScrollRegion = document.querySelector('.paywall-scroll-container');
                if (modalScrollRegion && modalScrollRegion.contains(e.target)) {
                    e.stopPropagation();
                    return true;
                }
                e.preventDefault();
                return false;
            }

            function detectMomentumScroll(e) {
                const now = Date.now();
                const deltaY = Math.abs(e.deltaY);
                
                // Clean up old events
                wheelEvents = wheelEvents.filter(event => now - event.time < 100);
                
                // Add current event
                wheelEvents.push({ deltaY, time: now });
                
                // Calculate momentum
                if (wheelEvents.length >= 2) {
                    const totalDelta = wheelEvents.reduce((sum, event) => sum + event.deltaY, 0);
                    const avgDelta = totalDelta / wheelEvents.length;
                    return avgDelta > 50; // High momentum threshold
                }
                
                return false;
            }

            function checkScroll(e) {
                if (preventScroll) {
                    if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    pinScroll();
                    return;
                }

                // Don't check if paywall is visible or animating
                if (isPaywallVisible) {
                    return;
                }

                const { shouldShow: paywallEnabled, isUserSubscriber } = shouldRenderPaywall();
                if (!paywallEnabled) {
                    return;
                }

                const articleContent = document.querySelector('.aw-article-content');
                if (!articleContent) {
                    return;
                }

                // Check if there's a Zephr element in the modal (try new ID first, then fallback to old ID)
                let modalContent = document.getElementById('adw-paywall-modal-content');
                if (!modalContent) {
                    modalContent = document.getElementById('adw-paywall-widgets');
                }
                if (!modalContent) {
                    return;
                }

                const zephrElement = modalContent.querySelector('.zephr-form-container, [id^="zephr-form-"]');
                if (!zephrElement) {
                    return;
                }

                const rect = articleContent.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                
            // Trigger when the end of the article reaches the point where 20% of article is left to scroll
            const articleHeight = rect.height;
            const articleTop = rect.top;
            const articleBottom = rect.bottom;
            const viewportHeight = window.innerHeight;
            
            // Apply/remove fade gradient based on user status
            // Always fade for non-subscribers; never fade for subscribers
            (function manageArticleFade() {
                const articleEl = articleContent;
                if (!articleEl) return;
                if (isUserSubscriber) {
                    if (articleEl.classList.contains('paywall-fade-gradient')) {
                        articleEl.classList.remove('paywall-fade-gradient');
                    }
                } else {
                    // Always show gradient for non-subscribers
                    if (!articleEl.classList.contains('paywall-fade-gradient')) {
                        articleEl.classList.add('paywall-fade-gradient');
                    }
                }
            })();

            const containerTop = getContainerViewportTop(scrollContainer);
            const containerScrollTop = getScrollTop(scrollContainer);
            const hasScrolledEnough = containerScrollTop > 100;
            const shouldShow = articleBottom <= containerTop + 200 && hasScrolledEnough;
                
                // Calculate scroll speed
                const now = Date.now();
                const timeDiff = Math.max(1, now - lastScrollCheck);
                const scrollDiff = Math.abs(containerScrollTop - lastScrollTop);
                scrollSpeed = scrollDiff / timeDiff;

                // Update last values
                lastScrollTop = containerScrollTop;
                lastScrollCheck = now;

                const isFastScrolling = scrollSpeed > 5.0;
                const hasMomentum = e && e.deltaY && detectMomentumScroll(e);
                
            // Show when article end is getting close to viewport bottom
            if (shouldShow) {
                    if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    lockScroll(getScrollTop(scrollContainer));
                }
            }

            function isInModalScrollRegion(target) {
                const region = document.querySelector('.paywall-scroll-container');
                return !!(region && target && region.contains(target));
            }

            function onScroll(e) {
                if (preventScroll) {
                    if (isInModalScrollRegion(e.target)) return;
                    pinScroll();
                    return;
                }
                requestAnimationFrame(() => checkScroll(e));
            }

            function onWheel(e) {
                if (preventScroll) {
                    if (isInModalScrollRegion(e.target)) return;
                    e.preventDefault();
                    pinScroll();
                    return;
                }
                checkScroll(e);
            }

            function onTouchMove(e) {
                if (preventScroll) {
                    if (isInModalScrollRegion(e.target)) return;
                    e.preventDefault();
                    pinScroll();
                    return;
                }
                checkScroll(e);
            }

            const attachedScrollTargets = new Set();
            function attachScrollListeners() {
                const targets = scrollContainer === window ? [window] : [window, scrollContainer];
                targets.forEach((t) => {
                    if (attachedScrollTargets.has(t)) return;
                    attachedScrollTargets.add(t);
                    t.addEventListener('scroll', onScroll, { passive: false, capture: true, signal });
                    t.addEventListener('wheel', onWheel, { passive: false, capture: true, signal });
                    t.addEventListener('touchmove', onTouchMove, { passive: false, capture: true, signal });
                });
            }
            attachScrollListeners();

            (function recheckScrollContainer(attempts = 0) {
                const resolved = getScrollContainer(document.querySelector('.aw-article-content'));
                if (resolved !== scrollContainer) {
                    scrollContainer = resolved;
                    attachScrollListeners();
                }
                if (resolved === window && attempts < 10) {
                    setTimeout(() => recheckScrollContainer(attempts + 1), 200);
                }
            })();

            // Handle escape key with signal
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && isPaywallVisible) {
                    hideEnhancedPaywall();
                }
            }, { signal });

            // Initial check
            setTimeout(checkScroll, 1000);

            // Dynamic content observer
            const observer = new MutationObserver(() => {
                checkScroll();
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Cleanup function
            function cleanup() {
                // Abort all event listeners
                controller.abort();
                // Disconnect observer
                observer?.disconnect();
                // Reset state
                isPaywallVisible = false;
                preventScroll = false;
                isLocking = false;
                wheelEvents = [];
            }

            // Add cleanup on beforeunload
            window.addEventListener('beforeunload', cleanup, { once: true });
        });
    }

    // Initialize when document is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            init();
        }, { once: true });
    } else {
        init();
    }
})(); 